// Port of Weather/METNorwayResponseStore.swift.
//
// The last MET Norway answer per request, so that METNorwayWeatherProvider follows api.met.no's terms: no repeat
// before `Expires`, then a conditional request (`If-Modified-Since` = the previous `Last-Modified`, verbatim) answered
// by `304 Not Modified` while the forecast is unchanged. An HTTP-level validator store, not the offline forecast cache
// (WeatherCache). It keeps the raw body so a 304 can be served from it, persisted in IndexedDB
// (`kvStore('weatherResponses')`) so it survives reloads.
//
// Bounded: at most `maxEntries` entries (the oldest `fetchedAt` goes first), and an entry older than `maxAge` (24 h) is
// dropped. Keys are the request query (`lat=46.95&lon=7.45&altitude=540`): the coarse location already sent.

import type { EpochMs } from '../core/models';
import { kvStore, type KeyValueStore } from '../storage/idb';

export interface METNorwayResponseEntry {
  /** The request query this answers, e.g. `lat=46.95&lon=7.45&altitude=540`. */
  key: string;
  /** Raw response body (Locationforecast GeoJSON text). */
  body: string;
  /** `Last-Modified` exactly as received, sent back as `If-Modified-Since`. */
  lastModified?: string;
  /** Device time before which MET asks not to repeat the request (from `Expires`). */
  expiresAt?: EpochMs;
  /** When MET last delivered or confirmed (304) this body. */
  fetchedAt: EpochMs;
}

function isEntry(value: unknown, key: string): value is METNorwayResponseEntry {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    e.key === key &&
    typeof e.body === 'string' &&
    typeof e.fetchedAt === 'number' &&
    Number.isFinite(e.fetchedAt) &&
    (e.lastModified === undefined || typeof e.lastModified === 'string') &&
    (e.expiresAt === undefined || (typeof e.expiresAt === 'number' && Number.isFinite(e.expiresAt)))
  );
}

export class METNorwayResponseStore {
  /** Maximum number of stored answers. */
  static readonly maxEntries = 32;
  /** Entries older than this (seconds since `fetchedAt`) are dropped. */
  static readonly maxAge = 24 * 3600;

  private entries?: Map<string, METNorwayResponseEntry>;
  /** No request to MET before this time (after a 429 / 403). In memory only. */
  private pausedUntil?: EpochMs;
  private queue: Promise<unknown> = Promise.resolve();

  /**
   * @param persistence Where entries are kept (default: IndexedDB store `weatherResponses`); `null` keeps them in
   *   memory only.
   */
  constructor(private readonly persistence: KeyValueStore<unknown> | null = kvStore<unknown>('weatherResponses')) {}

  /** The stored answer for `key`, unless it is older than `maxAge` at `now` (then it is removed). */
  entry(key: string, now: EpochMs): Promise<METNorwayResponseEntry | undefined> {
    return this.serial(async () => {
      const all = await this.load();
      const entry = all.get(key);
      if (!entry) return undefined;
      if ((now - entry.fetchedAt) / 1000 > METNorwayResponseStore.maxAge) {
        all.delete(key);
        await this.removePersisted(key);
        return undefined;
      }
      return entry;
    });
  }

  /** Stores (replaces) the answer for `entry.key`, evicting the oldest entries beyond `maxEntries`. */
  store(entry: METNorwayResponseEntry): Promise<void> {
    return this.serial(async () => {
      const all = await this.load();
      all.set(entry.key, entry);
      if (all.size > METNorwayResponseStore.maxEntries) {
        const overflow = [...all.values()]
          .sort((a, b) => a.fetchedAt - b.fetchedAt)
          .slice(0, all.size - METNorwayResponseStore.maxEntries);
        for (const old of overflow) {
          if (old.key === entry.key) continue;
          all.delete(old.key);
          await this.removePersisted(old.key);
        }
      }
      try {
        await this.persistence?.set(entry.key, { ...entry });
      } catch (error) {
        // Kept in memory; the next launch simply downloads again.
        console.error('Could not store MET Norway response', error);
      }
    });
  }

  /** Forgets the answer for `key` (e.g. a body that can no longer be parsed). */
  remove(key: string): Promise<void> {
    return this.serial(async () => {
      (await this.load()).delete(key);
      await this.removePersisted(key);
    });
  }

  /** Number of stored answers (diagnostics and tests). */
  count(): Promise<number> {
    return this.serial(async () => (await this.load()).size);
  }

  /** Stops requests to MET until `date`. A later pause extends an earlier one, never shortens it. */
  pauseRequests(until: EpochMs): void {
    this.pausedUntil = Math.max(this.pausedUntil ?? -Infinity, until);
  }

  /** The end of the current pause, or undefined when requests may be sent at `now`. */
  requestsPaused(now: EpochMs): EpochMs | undefined {
    return this.pausedUntil !== undefined && this.pausedUntil > now ? this.pausedUntil : undefined;
  }

  // MARK: Internals

  /** Runs operations one after another (the Swift actor's isolation). */
  private serial<T>(work: () => Promise<T>): Promise<T> {
    const next = this.queue.then(work, work);
    this.queue = next.catch(() => undefined);
    return next;
  }

  private async load(): Promise<Map<string, METNorwayResponseEntry>> {
    if (this.entries) return this.entries;
    const loaded = new Map<string, METNorwayResponseEntry>();
    if (this.persistence) {
      let rows: Array<[string, unknown]> = [];
      try {
        rows = await this.persistence.entries();
      } catch (error) {
        console.error('Could not read stored MET Norway responses', error);
      }
      for (const [key, value] of rows) {
        if (isEntry(value, key)) {
          loaded.set(key, value);
        } else {
          // Unreadable or misfiled: it can only cost one extra download, so drop it.
          await this.removePersisted(key);
        }
      }
    }
    this.entries = loaded;
    return loaded;
  }

  private async removePersisted(key: string): Promise<void> {
    try {
      await this.persistence?.delete(key);
    } catch {
      // Already gone or storage unavailable.
    }
  }
}
